import React from "react";
import { Row, Card, CardBody, Badge } from "reactstrap";
import { Colxx, Separator } from "../../../components/common/CustomBootstrap";
import IntlMessages from "../../../helpers/IntlMessages";
import { AddCommaNumber } from "../../../helpers/Utils";

const ContentComponent = ({
  stocks,
  products,
  data_outcome,
  data_income,
  data_order_table,
  data_order_payment,
  data_make_product,
  data_pnl_details,
  all,
}) => {
  const stocksRemaining = stocks?.data?.filter(
    (item) => item.count > 0,
  )?.length;

  const totalInventoryRemaining = stocks?.data?.reduce((acc, curr) => {
    const { avarage_price, count } = curr;
    return count > 0 && !isNaN(count) && !isNaN(avarage_price)
      ? Math.floor(acc + avarage_price * count)
      : acc;
  }, 0);

  const totalInventory = stocks?.data?.reduce(
    (acc, { total_value }) =>
      acc + (isNaN(total_value) ? 0 : Math.floor(total_value)),
    0,
  );

  const costStock = Number(data_make_product?.total_price || 0);

  // Calculate costStock for previous period by summing flip_value from each item
  const costStockFlip =
    data_make_product?.data?.reduce((acc, curr) => {
      return acc + Number(curr.compare_price?.flip_value || 0);
    }, 0) || 0;

  const filterOutcomeNotStock = data_outcome?.data?.filter(
    (item) => item.id !== 1,
  );

  const fixedCost = filterOutcomeNotStock?.reduce((acc, curr) => {
    return acc + Number(curr.price);
  }, 0);

  // Calculate fixed cost for previous period using flip_value from each item (excluding stock import - id: 1)
  const fixedCostFlip =
    filterOutcomeNotStock?.reduce((acc, curr) => {
      return acc + Number(curr.compare_price?.flip_value || 0);
    }, 0) || 0;

  // Calculate fixed cost comparison percentage
  const fixedCostChange = fixedCost - fixedCostFlip;
  const fixedCostPercentChange =
    fixedCostFlip === 0 ? 0 : (fixedCostChange * 100) / Math.abs(fixedCostFlip);
  const fixedCostCompare = {
    increment: fixedCostChange > 0,
    value: Math.round(fixedCostPercentChange * 100) / 100,
  };

  // Net Revenue = Income - Material Cost - Fixed Cost
  const netRevenue =
    Number(data_income.total_price) - costStock - Number(fixedCost);

  // Previous period values for net revenue
  const incomeFlip = Number(
    data_income.compare_total?.compare_price?.flip_value || 0,
  );
  const netRevenueFlip = incomeFlip - costStockFlip - fixedCostFlip;

  // Calculate net revenue comparison percentage
  const netRevenueChange = netRevenue - netRevenueFlip;
  const netRevenuePercentChange =
    netRevenueFlip === 0
      ? 0
      : (netRevenueChange * 100) / Math.abs(netRevenueFlip);
  const netRevenueCompare = {
    increment: netRevenueChange > 0,
    value: Math.round(netRevenuePercentChange * 100) / 100,
  };

  // =============== P&L DETAILED CALCULATIONS ===============

  // Gross Revenue (Doanh thu gộp) = từ order_payment (chưa trừ chiết khấu)
  const grossRevenue = Number(data_order_payment?.total_price || 0);

  // Income from other sources (Doanh thu khác) từ data_income - khác với doanh thu bán hàng
  const otherIncome =
    data_income?.data?.find((item) => item.id === "income")?.price || 0;

  // Net Revenue from Sales (Doanh thu ròng từ bán hàng) = grossRevenue (đã có chiết khấu trừ trong backend)
  const netRevenueFromSales = grossRevenue;

  // Data from PnL Details API - Thông tin chi tiết Chiết khấu, Thuế, Phụ phí, Chi phí quà tặng
  // Use nullish coalescing (??) instead of || to correctly handle 0 values
  const pnlDiscount = data_pnl_details?.totalDiscount ?? 0;
  const pnlTax = data_pnl_details?.totalTax ?? 0;
  const pnlFee = data_pnl_details?.totalFee ?? 0;
  const pnlGiftCost = data_pnl_details?.totalGiftCost ?? 0;
  const pnlGrossRevenue = data_pnl_details?.grossRevenue ?? grossRevenue;
  const pnlNetRevenue = data_pnl_details?.netRevenue ?? netRevenueFromSales;

  // Total Revenue (Tổng doanh thu) = Doanh thu bán hàng + Doanh thu khác
  const totalRevenue = Number(data_income.total_price);

  // COGS (Giá vốn hàng bán) = costStock (chi phí nguyên vật liệu sử dụng)
  const cogs = costStock;

  // Gross Profit (Lãi gộp) = Doanh thu ròng từ bán hàng - COGS
  const grossProfit = netRevenueFromSales - cogs;

  // Gross Profit Margin (Biên lãi gộp) = Lãi gộp / Doanh thu ròng × 100%
  const grossProfitMargin =
    netRevenueFromSales === 0 ? 0 : (grossProfit * 100) / netRevenueFromSales;

  // Operating Expenses (Chi phí hoạt động) = Chi phí cố định
  const operatingExpenses = fixedCost;

  // Operating Profit (Lợi nhuận hoạt động) = Lãi gộp - Chi phí hoạt động
  const operatingProfit = grossProfit - operatingExpenses;

  // Net Profit (Lợi nhuận ròng) = Lợi nhuận hoạt động + Thu nhập khác
  const netProfit = operatingProfit + otherIncome;

  // Net Profit Margin (Biên lợi nhuận ròng) = Lợi nhuận ròng / Tổng doanh thu × 100%
  const netProfitMargin =
    totalRevenue === 0 ? 0 : (netProfit * 100) / totalRevenue;

  return (
    <>
      <Card className="progress-banner w-100 mb-4">
        <CardBody>
          <Row className="d-flex align-items-center justify-content-center mb-3">
            <Colxx
              xxs="6"
              md="3"
              className="d-flex flex-column align-items-center justify-content-center mt-3 order-2 order-md-1"
            >
              <p>
                <IntlMessages id="analytics.revenue" />
              </p>
              <div className="d-flex flex-column flex-lg-row">
                <h5 className="mr-2 text-white">
                  {`${AddCommaNumber(data_income.total_price)} đ`}
                </h5>
              </div>
              {!all ? (
                <>
                  <Badge
                    color={
                      data_income.compare_total.compare_price.increment
                        ? "success"
                        : "danger"
                    }
                  >
                    {`${
                      data_income.compare_total.compare_price.increment
                        ? "+"
                        : ""
                    } ${data_income.compare_total.compare_price.value} %`}
                  </Badge>
                </>
              ) : null}
            </Colxx>
            <Colxx
              xxs="12"
              md="6"
              className="d-flex flex-column align-items-center justify-content-center order-1 order-md-2"
            >
              <h4>
                <IntlMessages id="analytics.profit" />
              </h4>
              <Row className="d-flex align-items-end justify-content-around w-50 mt-1 mb-2">
                {/* <h5 className='text-white mb-0'>
                                        {`${AddCommaNumber(
                                            get_income.total_price
                                        )} đ`}
                                    </h5>
                                    <p className='text-white mb-0'>{' / '}</p> */}
                <h2 className="text-white mb-0">
                  {`${AddCommaNumber(netRevenue)} đ`}
                </h2>
              </Row>
              <Row className="d-flex align-items-end justify-content-around w-50 mb-3">
                {!all ? (
                  <>
                    <Badge
                      color={netRevenueCompare.increment ? "success" : "danger"}
                    >
                      {`${netRevenueCompare.increment ? "+" : ""} ${
                        netRevenueCompare.value
                      } %`}
                    </Badge>
                  </>
                ) : null}
              </Row>
            </Colxx>
            <Colxx
              xxs="6"
              md="3"
              className="d-flex flex-column align-items-center justify-content-center mt-3 order-3 order-md-3"
            >
              <h4></h4>
              <p>
                <IntlMessages id="analytics.costs" />
              </p>
              <div className="d-flex flex-column flex-lg-row">
                <h5 className="mr-2 text-white">
                  {`${AddCommaNumber(fixedCost)} đ`}
                </h5>
              </div>
              {!all ? (
                <>
                  <Badge
                    color={fixedCostCompare.increment ? "success" : "danger"}
                  >
                    {`${fixedCostCompare.increment ? "+" : ""} ${
                      fixedCostCompare.value
                    } %`}
                  </Badge>
                </>
              ) : null}
            </Colxx>
          </Row>
          <Separator className="mb-3 mt-2" />
          <Row>
            <Colxx
              xxs="6"
              md="3"
              className="d-flex flex-column align-items-center justify-content-center mt-3 order-3 order-md-1"
            >
              <p>
                <IntlMessages id="analytics.average-selling-price" />
              </p>
              <div className="d-flex flex-column flex-lg-row">
                <h5 className="mr-2 text-white">
                  {data_order_payment?.total_count
                    ? AddCommaNumber(
                        (
                          parseFloat(data_order_payment?.total_price) /
                          data_order_payment?.total_count
                        ).toFixed(0),
                      )
                    : 0}{" "}
                  đ
                </h5>
              </div>
              {!all && data_order_payment?.compare_total?.compare_price
                ? (() => {
                    // Tính giá bán trung bình hiện tại
                    const currentAvg = data_order_payment?.total_count
                      ? parseFloat(data_order_payment?.total_price) /
                        data_order_payment?.total_count
                      : 0;
                    // Tính giá bán trung bình kỳ trước
                    const flipPrice =
                      data_order_payment?.compare_total?.compare_price
                        ?.flip_value || 0;
                    const flipCount =
                      data_order_payment?.compare_total?.compare_count
                        ?.flip_value || 0;
                    const flipAvg =
                      flipCount > 0 ? parseFloat(flipPrice) / flipCount : 0;
                    // Tính % thay đổi
                    const changeAvg = currentAvg - flipAvg;
                    const percentChange =
                      flipAvg === 0 ? 0 : (changeAvg * 100) / flipAvg;
                    const isIncrement = changeAvg > 0;
                    const displayValue = Math.round(percentChange * 100) / 100;

                    return (
                      <Badge color={isIncrement ? "success" : "danger"}>
                        {`${isIncrement ? "+" : ""} ${displayValue} %`}
                      </Badge>
                    );
                  })()
                : null}
            </Colxx>
            <Colxx
              xxs="6"
              md="3"
              className="d-flex flex-column align-items-center justify-content-center mt-3 order-2 order-md-2"
            >
              <p>
                <IntlMessages id="analytics.number-products-sold" />
              </p>
              <div className="d-flex flex-column flex-lg-row">
                <h5 className="mr-2 text-white">
                  {`${data_order_payment.total_count} sản phẩm`}
                </h5>
              </div>
              {!all ? (
                <Badge
                  color={
                    data_order_payment.compare_total.compare_count.increment
                      ? "success"
                      : "danger"
                  }
                >
                  {`${
                    data_order_payment.compare_total.compare_count.increment
                      ? "+"
                      : ""
                  } ${data_order_payment.compare_total.compare_count.value} %`}
                </Badge>
              ) : null}
            </Colxx>
            <Colxx
              xxs="6"
              md="3"
              className="d-flex flex-column align-items-center justify-content-center mt-3 order-1 order-md-3"
            >
              <p>
                <IntlMessages id="analytics.invoice-quantity" />
              </p>
              <div className="d-flex flex-column flex-lg-row">
                <h5 className="mr-2 text-white">
                  {`${data_order_table.total_table_has_guest} hoá đơn`}
                </h5>
              </div>
              {!all ? (
                <Badge
                  color={
                    data_order_table.compare_total.compare_count.increment
                      ? "success"
                      : "danger"
                  }
                >
                  {`${
                    data_order_table.compare_total.compare_count.increment
                      ? "+"
                      : ""
                  } ${data_order_table.compare_total.compare_count.value} %`}
                </Badge>
              ) : null}
            </Colxx>
            <Colxx
              xxs="6"
              md="3"
              className="d-flex flex-column align-items-center justify-content-center mt-3 order-4 order-md-4"
            >
              <p>
                <IntlMessages id="analytics.total-product" />
              </p>
              <div className="d-flex flex-column flex-lg-row">
                <h5 className="mr-2 text-white">
                  {products?.data?.length}{" "}
                  <IntlMessages id="analytics.product" />
                </h5>
              </div>
            </Colxx>
            <Colxx
              xxs="6"
              md="3"
              className="d-flex flex-column align-items-center justify-content-center mt-3 order-5 order-md-5"
            >
              <p>
                <IntlMessages id="analytics.amount-of-inventory" />
              </p>
              <div className="d-flex flex-column flex-lg-row">
                <h5 className="mr-2 text-white">{stocksRemaining} sản phẩm</h5>
              </div>
            </Colxx>
            <Colxx
              xxs="6"
              md="3"
              className="d-flex flex-column align-items-center justify-content-center mt-3 order-6 order-md-6"
            >
              <p>
                <IntlMessages id="analytics.inventory-value" />
              </p>
              <div className="d-flex flex-column flex-lg-row">
                <h5 className="mr-2 text-white">
                  {AddCommaNumber(totalInventoryRemaining)} đ
                </h5>
              </div>
            </Colxx>
            <Colxx
              xxs="6"
              md="3"
              className="d-flex flex-column align-items-center justify-content-center mt-3 order-7 order-md-7 border-right-above-md"
            >
              <p>
                <IntlMessages id="analytics.imported-stock-quantity" />
              </p>
              <div className="d-flex flex-column flex-lg-row">
                <h5 className="mr-2 text-white">
                  {stocks?.data?.length} sản phẩm
                </h5>
              </div>
            </Colxx>
            <Colxx
              xxs="6"
              md="3"
              className="d-flex flex-column align-items-center justify-content-center mt-3 order-8 order-md-8 border-right-above-md "
            >
              <p>
                <IntlMessages id="analytics.total-stock-value" />
              </p>
              <div className="d-flex flex-column flex-lg-row">
                <h5 className="mr-2 text-white">
                  {AddCommaNumber(totalInventory)} đ
                </h5>
              </div>
            </Colxx>
          </Row>
        </CardBody>
      </Card>

      {/* ============ P&L DETAILED STATEMENT ============ */}
      <Card className="w-100 mb-4">
        <CardBody>
          <h5 className="mb-4 font-weight-bold text-muted">
            <i className="simple-icon-chart mr-2"></i>
            <IntlMessages id="analytics.pnl.title" />
          </h5>

          <Row>
            {/* ========== CỘT TRÁI: DOANH THU ========== */}
            <Colxx xxs="12" md="6">
              <div
                style={{
                  // backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  padding: "20px",
                  // border: "1px solid #e9ecef",
                  height: "100%",
                }}
              >
                {/* Header Doanh thu */}
                <div
                  style={{
                    fontWeight: "bold",
                    color: "#28a745",
                    marginBottom: "12px",
                    fontSize: "16px",
                    borderBottom: "2px solid #28a745",
                    paddingBottom: "8px",
                  }}
                >
                  <IntlMessages id="analytics.pnl.revenue-header" />
                </div>

                {/* Doanh thu bán hàng */}
                <div className="pnl-row">
                  <span>
                    <IntlMessages id="analytics.pnl.sales-revenue" />
                  </span>
                  <span style={{ fontWeight: "600" }}>
                    {AddCommaNumber(grossRevenue)} đ
                  </span>
                </div>

                {/* Chi tiết: Doanh thu gộp -> Chiết khấu -> Thuế -> Phụ phí */}
                <div className="pnl-item">
                  <span style={{ fontStyle: "italic", color: "#6c757d" }}>
                    <IntlMessages id="analytics.pnl.gross-revenue" />
                  </span>
                  <span style={{ fontWeight: "600", color: "#6c757d" }}>
                    {AddCommaNumber(pnlGrossRevenue)} đ
                  </span>
                </div>

                {/* Chiết khấu (trừ ra) */}
                <div className="pnl-item">
                  <span style={{ fontStyle: "italic", color: "#dc3545" }}>
                    <IntlMessages id="analytics.pnl.discount" />
                  </span>
                  <span style={{ fontWeight: "600", color: "#dc3545" }}>
                    - {AddCommaNumber(pnlDiscount)} đ
                  </span>
                </div>

                {/* Thuế */}
                <div className="pnl-item">
                  <span style={{ fontStyle: "italic", color: "#28a745" }}>
                    <IntlMessages id="analytics.pnl.tax" />
                  </span>
                  <span style={{ fontWeight: "600", color: "#28a745" }}>
                    + {AddCommaNumber(pnlTax)} đ
                  </span>
                </div>

                {/* Phí khác */}
                <div className="pnl-item">
                  <span style={{ fontStyle: "italic", color: "#28a745" }}>
                    <IntlMessages id="analytics.pnl.other-fees" />
                  </span>
                  <span style={{ fontWeight: "600", color: "#28a745" }}>
                    + {AddCommaNumber(pnlFee)} đ
                  </span>
                </div>

                {/* Doanh thu khác */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 0",
                    borderBottom: "1px dashed #dee2e6",
                  }}
                >
                  <span>
                    <IntlMessages id="analytics.pnl.other-income" />
                  </span>
                  <span style={{ fontWeight: "600" }}>
                    {AddCommaNumber(otherIncome)} đ
                  </span>
                </div>

                {/* Tổng doanh thu */}
                <div className="pnl-result-box bg-success-soft">
                  <span style={{ fontWeight: "bold" }}>
                    <IntlMessages id="analytics.pnl.total-revenue" />
                  </span>
                  <span style={{ fontWeight: "bold", color: "#28a745" }}>
                    {AddCommaNumber(totalRevenue)} đ
                  </span>
                </div>
              </div>
            </Colxx>

            {/* ========== CỘT PHẢI: CHI PHÍ ========== */}
            <Colxx xxs="12" md="6">
              <div
                style={{
                  // backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  padding: "20px",
                  // border: "1px solid #e9ecef",
                  height: "100%",
                }}
              >
                {/* Header Chi phí */}
                <div
                  style={{
                    fontWeight: "bold",
                    color: "#dc3545",
                    marginBottom: "12px",
                    fontSize: "16px",
                    borderBottom: "2px solid #dc3545",
                    paddingBottom: "8px",
                  }}
                >
                  <IntlMessages id="analytics.pnl.cost-header" />
                </div>

                {/* Giá vốn hàng bán */}
                <div
                  style={{
                    fontWeight: "600",
                    color: "#6c757d",
                    marginBottom: "8px",
                    marginTop: "12px",
                    fontSize: "13px",
                  }}
                >
                  <IntlMessages id="analytics.pnl.cogs" />
                </div>
                <div className="pnl-item">
                  <span>
                    <IntlMessages id="analytics.pnl.material-cost" />
                  </span>
                  <span style={{ fontWeight: "600", color: "#dc3545" }}>
                    - {AddCommaNumber(cogs)} đ
                  </span>
                </div>

                {/* Lãi gộp */}
                <div className="pnl-result-box bg-warning-soft mb-3">
                  <span style={{ fontWeight: "bold" }}>
                    <IntlMessages id="analytics.pnl.gross-profit" />
                  </span>
                  <span
                    style={{
                      fontWeight: "bold",
                      color: grossProfit >= 0 ? "#28a745" : "#dc3545",
                    }}
                  >
                    {AddCommaNumber(grossProfit)} đ
                    <span
                      style={{
                        fontSize: "12px",
                        marginLeft: "8px",
                        opacity: 0.8,
                      }}
                    >
                      ({grossProfitMargin.toFixed(1)}%)
                    </span>
                  </span>
                </div>

                {/* Chi phí hoạt động */}
                <div
                  style={{
                    fontWeight: "600",
                    color: "#6c757d",
                    marginBottom: "8px",
                    fontSize: "13px",
                  }}
                >
                  <IntlMessages id="analytics.pnl.operating-expenses" />
                </div>
                <div className="pnl-item">
                  <span>
                    <IntlMessages id="analytics.pnl.fixed-costs" />
                  </span>
                  <span style={{ fontWeight: "600", color: "#dc3545" }}>
                    - {AddCommaNumber(operatingExpenses)} đ
                  </span>
                </div>

                {/* Chi phí quà tặng khuyến mãi */}
                <div className="pnl-item">
                  <span>
                    <IntlMessages id="analytics.pnl.gift-cost" />
                  </span>
                  <span style={{ fontWeight: "600", color: "#dc3545" }}>
                    - {AddCommaNumber(pnlGiftCost)} đ
                  </span>
                </div>

                {/* Tổng chi phí */}
                <div className="pnl-result-box bg-danger-soft">
                  <span style={{ fontWeight: "bold" }}>
                    <IntlMessages id="analytics.pnl.total-expenses" />
                  </span>
                  <span style={{ fontWeight: "bold", color: "#dc3545" }}>
                    - {AddCommaNumber(cogs + operatingExpenses + pnlGiftCost)} đ
                  </span>
                </div>
              </div>
            </Colxx>
          </Row>

          {/* ========== LỢI NHUẬN RÒNG (Full width) ========== */}
          <div
            className={`pnl-net-result ${netProfit >= 0 ? "bg-success-soft border-success-2" : "bg-danger-soft border-danger-2"}`}
          >
            <span style={{ fontWeight: "bold", fontSize: "16px" }}>
              {netProfit >= 0 ? (
                <IntlMessages id="analytics.pnl.net-profit" />
              ) : (
                <IntlMessages id="analytics.pnl.net-loss" />
              )}
            </span>
            <span
              style={{
                fontWeight: "bold",
                fontSize: "18px",
                color: netProfit >= 0 ? "#28a745" : "#dc3545",
              }}
            >
              {AddCommaNumber(Math.abs(netProfit))} đ
              <span
                style={{ fontSize: "14px", marginLeft: "8px", opacity: 0.8 }}
              >
                ({netProfitMargin.toFixed(1)}%)
              </span>
            </span>
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default ContentComponent;
